import { useMemo } from 'react';
import { DateLocalizer, EventPropGetter, View } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import Drawer from 'react-modern-drawer';

import FilterPanel from 'components/filter-panel';
import { Row, Typography } from 'enable-ui';

import { EventForm } from 'modules/calendar/components/EventForm';
import { useCalendarController } from 'modules/calendar/core/controllers';
import { EventType } from 'modules/calendar/core/enum';
import { EventSession } from 'modules/calendar/core/type';
import { createFilterConfig, filterEvents } from 'modules/calendar/helpers';
import useEventStore from 'modules/calendar/store/event';
import { CalendarContainer, FilterPanelContainer, PillButton } from 'modules/calendar/styles';
import { CustomToolBar, DragAndDropCalendar, localizer } from 'modules/classes/page/CalendarPage';

import { CalendarWrapper } from '../../classes/styles/calendar';

import { format } from 'date-fns';
import { useString } from 'hooks';

import { COLOR, COLOR_CONFIG } from 'constants/color';

export const eventTypesPillButton = (label: string, color: string) => (
  <Row>
    <PillButton color={color} style={{ margin: '3px 8px 0 0' }} />
    <Typography variant="body-2" color={COLOR.surfaceNeutralBold['neutral-11']}>
      {label}
    </Typography>
  </Row>
);

const CustomEvent = (event: any, view: View) => {
  const isViewMonth = view === 'month';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: isViewMonth ? 'row' : 'column',
        width: '100%',
        height: '100%',
      }}
    >
      <Typography
        style={
          !isViewMonth
            ? {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '100%',
                fontSize: 14,
              }
            : {}
        }
      >
        {event.title}
      </Typography>
      {!isViewMonth && (
        <Typography style={{ fontSize: 12, marginRight: 4, fontWeight: 400, width: '100%' }}>
          {format(String(event.event.event.start), 'h:mm a')} -{' '}
          {format(String(event.event.event.end), 'h:mm a')}
        </Typography>
      )}
    </div>
  );
};

export const CalendarPage = () => {
  const {
    view,
    currentDate,
    setFilters,
    setView,
    handleSelectSlot,
    handleDateChange,
    onNavigate,
    drawer,
    filters,
    classesState,
    loadMoreClasses,
    handleFilterSearch,
    setCurrentDate,
  } = useCalendarController();

  const events = useEventStore((state) => state.events);

  const selectedEventSessionId = useString('');

  const calendarFormats = {
    weekdayFormat: (wd: Date, culture: string | undefined, loc: DateLocalizer | undefined) =>
      (loc ?? localizer).format(wd, 'EEE', culture),
  };

  const eventStyleGetter = (record: EventSession) => {
    const type = record.event.type;

    const typeStyles = {
      [EventType.Class]: {
        bg: COLOR.purpel['purpel-3'],
        border: COLOR.purpel['purpel-6'],
        className: 'text-class-lesson',
      },
      [EventType.Gamification]: {
        bg: COLOR_CONFIG.cyan,
        border: COLOR_CONFIG.cyan,
        className: 'text-challenge',
      },
      [EventType.Other]: {
        bg: '#ffe0ed',
        border: COLOR_CONFIG.magenta,
        className: 'text-other-events',
      },
    };

    return {
      className: typeStyles[type]?.className,
      style: {
        backgroundColor: typeStyles[type]?.bg,
        border: `none`,
        borderLeft: `3px solid ${typeStyles[type]?.border}`,
      },
    };
  };

  const filterConfig = useMemo(
    () => createFilterConfig(events, classesState.classes),
    [events, classesState.classes],
  );

  const filteredEvents = useMemo(() => filterEvents(events, filters), [events, filters]);

  const loadMoreHandlers = useMemo(
    () => ({
      classes: {
        loadMore: loadMoreClasses,
        hasMore: classesState.hasMore,
        isLoading: classesState.isLoading,
      },
    }),
    [loadMoreClasses, classesState.hasMore, classesState.isLoading],
  );

  return (
    <Row className="full-height">
      <CalendarContainer align="center" className="full-width" view={view}>
        <CalendarWrapper className="full-width">
          <DragAndDropCalendar
            className={
              view === 'month'
                ? ''
                : `custom-calendar-time ${view === 'day' ? 'custom-calendar-day' : ''}`
            }
            localizer={localizer}
            date={currentDate}
            popup={false}
            doShowMoreDrillDown={false}
            onSelectEvent={(event: any) => {
              selectedEventSessionId.setValue(event.id);
              drawer.setValue(true);
            }}
            events={filteredEvents}
            formats={calendarFormats}
            components={{
              toolbar: CustomToolBar,
              event: (event) => CustomEvent(event, view),
            }}
            view={view}
            onShowMore={handleDateChange}
            onView={setView}
            eventPropGetter={eventStyleGetter as EventPropGetter<object> | undefined}
            onNavigate={onNavigate}
            onSelectSlot={handleSelectSlot}
            selectable={true}
            showMultiDayTimes={true}
          />
        </CalendarWrapper>
      </CalendarContainer>

      <FilterPanelContainer>
        <FilterPanel
          items={filterConfig}
          onFilterChange={setFilters}
          loadMoreHandlers={loadMoreHandlers}
          onSearch={handleFilterSearch}
        />
      </FilterPanelContainer>

      <Drawer
        open={drawer.value}
        onClose={() => drawer.setValue(false)}
        direction="right"
        style={{ width: '100%', maxWidth: 588 }}
      >
        <EventForm
          classes={classesState.classes}
          view={view}
          drawer={drawer}
          eventSession={selectedEventSessionId}
          selectedDate={currentDate}
          setCurrentDate={setCurrentDate}
        />
      </Drawer>
    </Row>
  );
};
